<template>
	<div class="row">
		<div class="col-12 d-flex flex-row align-items-center justify-content-between">
			<h1>{{ $t('configuration-settings') }}</h1>
			<button @click="save" class="btn btn-primary mb-3" :disabled="submitting || !dirty || hasInvalidJson">{{ $t('save') }}</button>
		</div>

		<div class="col-12">
			<p>{{ $t('configuration-settings-description') }}</p>

			<Table
				:loading="loading"
				:submitting="submitting"
				:cols="[
					{ key: 'group', heading: $t('group'), size: 2 },
					{ key: 'name', heading: $t('name'), size: 3 },
					{ key: 'type', heading: $t('type'), size: 1 },
					{ key: 'value', heading: $t('value'), size: 6 }
				]"
				:data="rows"
				breakpoint="md"
				:filters="{ ...filters }"
				:searchable="true"
				:scrollPaginate="true"
				@refresh="refresh"
				@toPage="toPage"
			>
				<template v-slot:col-value="{ item }">
					<YesNoToggle
						v-if="item.type === 'BOOL'"
						:label="$t('value')"
						:srOnly="true"
						v-model="item.valueDraft"
						@input="onValueChange"
					/>
					<input
						v-else-if="item.type === 'INT' || item.type === 'DOUBLE'"
						type="number"
						class="form-control"
						:value="item.valueDraft"
						@input="onValueInput(item, $event.target.value)"
					/>
					<div v-else-if="item.type === 'JSON'" class="d-flex align-items-start" style="gap: 0.25rem">
						<div class="flex-grow-1">
							<TextAreaAutosize
								:label="$t('value')"
								:srOnly="true"
								:maxHeight="200"
								:error="!!item.valueError"
								:value="item.valueDraft"
								@input="value => onValueInput(item, value)"
							/>
							<div v-if="item.valueError" class="text-danger small mt-n2 mb-2">{{ item.valueError }}</div>
						</div>
						<button
							type="button"
							class="btn btn-sm btn-outline-secondary"
							:aria-label="$t('expand')"
							:title="$t('expand')"
							@click="openExpandModal(item)"
						>
							<b-icon icon="arrows-fullscreen" />
						</button>
					</div>
					<input
						v-else
						type="text"
						class="form-control"
						:value="item.valueDraft"
						@input="onValueInput(item, $event.target.value)"
					/>
				</template>
			</Table>
		</div>

		<b-modal v-model="expandModal.show" :title="$t('value')" size="lg" @shown="onExpandModalShown">
			<TextAreaAutosize
				ref="expandTextarea"
				:label="$t('value')"
				:srOnly="true"
				:error="!!expandModal.error"
				:value="expandModal.draft"
				@input="validateExpandModal"
			/>
			<div v-if="expandModal.error" class="text-danger small mt-n2">{{ expandModal.error }}</div>

			<template v-slot:modal-footer>
				<div class="d-flex">
					<button type="button" class="btn btn-primary mr-2" :disabled="!!expandModal.error" @click="saveExpandModal">{{ $t('save') }}</button>
					<button type="button" class="btn btn-outline-secondary" @click="expandModal.show = false">{{ $t('cancel') }}</button>
				</div>
			</template>
		</b-modal>
	</div>
</template>

<script>
import Table from "src/components/Table/Table.vue";
import YesNoToggle from "src/components/Inputs/YesNoToggle";
import TextAreaAutosize from "src/components/Inputs/TextAreaAutosize";

export default {
	components: {
		Table,
		YesNoToggle,
		TextAreaAutosize
	},
	data() {
		return {
			loading: true,
			submitting: false,
			dirty: false,
			allRows: [],
			filteredRows: [],
			rows: [],
			filters: {
				page: 1,
				page_size: 30,
				total: 0,
				search: ''
			},
			expandModal: {
				show: false,
				item: null,
				draft: '',
				error: null
			}
		};
	},
	mounted() {
		this.loadList();
	},
	methods: {
		loadList() {
			this.loading = true;

			return this.$cfetch(`${this.$settings.APIBaseURL}/configsettings/list`, {
				method: 'GET'
			})
				.then(res => {
					if (res.status !== 'SUCCESS') throw new Error('Unexpected error fetching config settings');

					this.allRows = res.result.map(row => ({
						id: `${row.group}.${row.name}`,
						group: row.group,
						name: row.name,
						type: row.type,
						value: row.value,
						valueDraft: row.type === 'BOOL' ? Number(row.value) === 1 : row.value,
						valueError: null
					}));

					this.dirty = false;
					this.filters = { ...this.filters, page: 1, search: '' };
					this.getList();
				})
				.catch(error => {
					this.$log.error.call(this, error);
					this.$store.commit('openDialog', {
						title: this.$t('something-went-wrong'),
						content: this.$t('error-fetching-config-settings')
					});
				})
				.finally(() => {
					this.loading = false;
				});
		},
		onValueInput(item, value) {
			if (item.valueDraft === value) return;
			item.valueDraft = value;
			item.valueError = item.type === 'JSON' ? this.validateJSON(value) : null;
			this.dirty = true;
		},
		onValueChange() {
			this.dirty = true;
		},
		validateJSON(value) {
			if ((value || '').trim() === '') return null;
			try {
				JSON.parse(value);
				return null;
			} catch (error) {
				return this.$t('invalid-json', { message: error.message });
			}
		},
		openExpandModal(item) {
			this.expandModal = {
				show: true,
				item,
				draft: item.valueDraft,
				error: item.valueError
			};
		},
		validateExpandModal(value) {
			this.expandModal.draft = value;
			this.expandModal.error = this.validateJSON(value);
		},
		onExpandModalShown() {
			if (this.$refs.expandTextarea) this.$refs.expandTextarea.resize();
		},
		saveExpandModal() {
			if (this.expandModal.error) return;
			this.onValueInput(this.expandModal.item, this.expandModal.draft);
			this.expandModal.show = false;
		},
		getList() {
			const term = (this.filters.search || '').trim().toLowerCase();

			this.filteredRows = term
				? this.allRows.filter(row => row.group.toLowerCase().includes(term) || row.name.toLowerCase().includes(term))
				: this.allRows;

			this.filters.total = this.filteredRows.length;

			const start = (this.filters.page - 1) * this.filters.page_size;
			this.rows = this.filteredRows.slice(start, start + this.filters.page_size);
		},
		refresh(newFilters) {
			this.filters = { ...this.filters, ...newFilters };
			this.getList();
		},
		toPage(pageNo) {
			this.filters.page = pageNo;
			this.getList();
		},
		isRowDirty(row) {
			return row.type === 'BOOL' ? row.valueDraft !== (Number(row.value) === 1) : row.valueDraft !== row.value;
		},
		save() {
			if (this.submitting || this.hasInvalidJson) return;
			this.submitting = true;

			this.$store.commit('showBlockingSpinner');

			const changes = this.allRows
				.filter(row => this.isRowDirty(row))
				.map(row => ({
					group: row.group,
					name: row.name,
					value: row.type === 'BOOL' ? (row.valueDraft ? '1' : '0') : row.valueDraft
				}));

			this.$cfetch(`${this.$settings.APIBaseURL}/configsettings/save`, {
				method: 'post',
				data: { changes }
			})
				.then(res => {
					if (res.status !== 'SUCCESS') throw new Error('Unexpected error saving config settings');
					return this.loadList();
				})
				.then(() => {
					this.$store.commit('openDialog', {
						title: this.$t('success'),
						content: this.$t('config-settings-saved')
					});
				})
				.catch(error => {
					this.$log.error.call(this, error);
					this.$store.commit('openDialog', {
						title: this.$t('something-went-wrong'),
						content: this.$t('error-saving-config-settings')
					});
				})
				.finally(() => {
					this.$store.commit('hideBlockingSpinner');
					this.submitting = false;
				});
		}
	},
	computed: {
		hasInvalidJson() {
			return this.allRows.some(row => !!row.valueError);
		}
	}
};
</script>
